import { useEffect, useState } from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { getFixcenterProductLevelAttributes } from '../../../services/products.service';
import { FeatureType } from '../../../shared/models/feature-type.model';
import OrderingRangeSlider from './OrderingRangeSlider';
import OrderingDropdownFilter from './OrderingDropdownFilter';
import { IFeature } from '../../product-filters-menu/interfaces/feature.interface';
import { IFeatureDiameterDto } from '../../product-filters-menu/interfaces/feature-diameter-dto.interface';
import { IProductLevelFilteredFeatureDto } from '../../product-filters-menu/interfaces/product-level-filtered-feature-dto.interface';
import OrderingDiameterFilter from './OrderingDiameterFilter';
import { diameterFilterExist } from '../../product-filters-menu/diameter-filter.service';
import { useBetween } from 'use-between';
import { ProductFiltersUseBetween } from '../../product-filters-menu/use-between/product-filters-use-between';
import { getRangeFilter } from '../../product-filters-menu/product-filters.service';
import OrderingTableFiltersSkeleton from './OrderingTableFiltersSkeleton';
import { ProductFeatureDetailsDto } from '../../../shared/models/product-feature-details-dto.model';
import { filterItemSx, filterLabelSx } from './ordering-table-filters.styles';

export interface OrderingTableFiltersProps {
    productId: string;
    categoryId: string;
    onFiltersLoaded: (
        filters: IFeature[],
        variantFeatureValues: Record<string, ProductFeatureDetailsDto[]>,
        diameterFilter?: IFeatureDiameterDto
    ) => void;
}

export default function OrderingTableFilters({ productId, categoryId, onFiltersLoaded }: OrderingTableFiltersProps) {
    const [filters, setFilters] = useState<IFeature[]>([]);
    const [diameterFilter, setDiameterFilter] = useState<IFeatureDiameterDto>();
    const [isSkeletonLoader, setIsSkeletonLoader] = useState<boolean>(false);
    const { setFeaturesFilters } = useBetween(ProductFiltersUseBetween);

    useEffect(() => {
        if (productId && categoryId) {
            fetchProductLevelAttributes();
        }
    }, [productId, categoryId]);

    const fetchProductLevelAttributes = () => {
        setIsSkeletonLoader(true);
        getFixcenterProductLevelAttributes(productId, categoryId).then((response: any): void => {
            const dto: IProductLevelFilteredFeatureDto = response.data;
            const data = (dto.data ?? []) as IFeature[];
            const diameter = dto.diameter as IFeatureDiameterDto;
            const variantFeatureValues = dto.variantFeatureValues ?? {};
            setFilters(data);
            setDiameterFilter(diameter);
            onFiltersLoaded(data, variantFeatureValues, diameter);
            setIsSkeletonLoader(false);
        });
    };

    const handleEnumValuesChange = (featureId: string, enumValues: string[] | boolean) => {
        setFeaturesFilters((prevFilters) => {
            const existingFilterIndex = prevFilters.findIndex((f) => f.featureId === featureId);

            if (Array.isArray(enumValues) && enumValues.length > 0) {
                const updatedFilter = { featureId, valueType: 'enum' as const, values: enumValues };
                if (existingFilterIndex >= 0) {
                    return prevFilters.map((f, index) => (index === existingFilterIndex ? updatedFilter : f));
                }
                return [...prevFilters, updatedFilter];
            }

            if (typeof enumValues === 'boolean') {
                if (!enumValues) {
                    return prevFilters.filter((_, index) => index !== existingFilterIndex);
                }
                const updatedFilter = { featureId, valueType: 'boolean' as const, booleanValue: enumValues };
                if (existingFilterIndex >= 0) {
                    return prevFilters.map((f, index) => (index === existingFilterIndex ? updatedFilter : f));
                }
                return [...prevFilters, updatedFilter];
            }

            return prevFilters.filter((_, index) => index !== existingFilterIndex);
        });
    };

    const handleRangeChange = (featureId: string, rangeFrom: string, rangeTo: string) => {
        setFeaturesFilters((prevFilters) => getRangeFilter(featureId, rangeFrom, rangeTo, prevFilters));
    };

    const getFilterType = (filter: IFeature) => FeatureType[filter.featureType];
    const getFilterName = (filter: IFeature) => filter.labelInEnglish;

    if (isSkeletonLoader) {
        return <OrderingTableFiltersSkeleton />;
    }

    if (filters.length === 0 && !diameterFilterExist(diameterFilter as IFeatureDiameterDto)) {
        return null;
    }

    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Stack direction="row" flexWrap="wrap" alignItems="flex-start" sx={{ gap: 2 }}>
                {diameterFilterExist(diameterFilter as IFeatureDiameterDto) && (
                    <OrderingDiameterFilter diameterFilter={diameterFilter as IFeatureDiameterDto} />
                )}
                {filters.map((filter) => (
                    <Box key={filter.id} sx={filterItemSx}>
                        <Typography variant="body2" noWrap title={getFilterName(filter)} sx={filterLabelSx}>
                            {getFilterName(filter)}
                        </Typography>
                        {getFilterType(filter) === 'NumericRange' && (
                            <OrderingRangeSlider
                                min={filter.minValue}
                                max={filter.maxValue}
                                onChange={() => {}}
                                name={getFilterName(filter)}
                                featureId={filter.id}
                                onRangeChange={handleRangeChange}
                            />
                        )}
                        {(getFilterType(filter) === 'Boolean' || getFilterType(filter) === 'Enum') && (
                            <OrderingDropdownFilter
                                values={filter.availableEnumValues ?? []}
                                name={getFilterName(filter)}
                                featureId={filter.id}
                                onEnumValuesChange={handleEnumValuesChange}
                                isBoolean={getFilterType(filter) === 'Boolean'}
                            />
                        )}
                        {(getFilterType(filter) === 'Text' || getFilterType(filter) === 'Numeric') && (
                            <OrderingDropdownFilter
                                values={filter.availableEnumValues ?? []}
                                name={getFilterName(filter)}
                                featureId={filter.id}
                                onEnumValuesChange={handleEnumValuesChange}
                                isBoolean={false}
                            />
                        )}
                    </Box>
                ))}
            </Stack>
        </Paper>
    );
}
